import * as React from "react";
import { view } from "@orderofchaos/mvvm";
import { Layout } from "../../components/Layout/Layout";
import styles from "./AddProductPage.module.css";
import Button, { ButtonDesign } from "../../components/Button/Button";
import FieldSet from "../../components/FieldSet/FieldSet";
import Input from "../../components/Input/Input";
import { PickADayInput } from "../../components/PickADay/PickADayInput";
import { TypeTagsInput } from "../../components/TypeTagsInput/TypeTagsInput";
import { ProductPhotoSlider } from "../../components/ProductPhotoSlider/ProductPhotoSlider";
import Icon, { IconSize } from "../../components/Icon/Icon";
import { ConfirmDialog } from "../../components/ConfirmDialog/ConfirmDialog";
import { QrScannerOverlay } from "../../components/QrScannerOverlay/QrScannerOverlay";
import { BoxReferenceInput } from "../../components/BoxReferenceInput/BoxReferenceInput";
import {
  AddProductPageViewModel,
  type AddProductPageViewProps,
} from "./AddProductPageViewModel";
import { Measures } from "@/types";

interface AddProductPageProps extends AddProductPageViewProps {}

export const AddProductPage: React.FC<AddProductPageProps> = view(
  AddProductPageViewModel,
)<AddProductPageProps>(({
  viewModel: {
    title,
    photo,
    photos,
    name,
    description,
    typeTags,
    typeTagInput,
    parentId,
    manufacturingDate,
    expiryDate,
    measure,
    quantityInput,
    qrPayloadInput,
    qrBindingError,
    isQrScannerOpen,
    isQrScanResolving,
    qrScannerDelay,
    error,
    referenceError,
    isReferenceLoading,
    isPhotoLoading,
    canAddMorePhotos,
    canRemoveActivePhoto,
    photoCounterLabel,
    typeTagSuggestions,
    parentIdSuggestions,
    isSubmitting,
    canSubmit,
    voiceTranscript,
    isVoicePanelOpen,
    isVoiceSupported,
    isVoiceListening,
    isVoiceApplying,
    voiceError,
    voiceInfo,
    canStartVoiceListening,
    canStopVoiceListening,
    canApplyVoiceTranscript,
    canApplyPhotoAutofill,
    isAiPhotoAutofillEnabled,
    isAiPhotoSuggestionOpen,
    aiPhotoSuggestionDescription,
    onNameChange,
    onDescriptionChange,
    onPhotoFileChange,
    removeActivePhoto,
    onPhotoSliderActiveIndexChange,
    onVoiceTranscriptChange,
    onVoicePrimaryButtonClick,
    closeVoicePanel,
    clearVoiceTranscript,
    setTypeTagInput,
    addTypeTags,
    removeTypeTag,
    setParentIdInput,
    setManufacturingDateValue,
    setExpiryDateValue,
    setMeasure,
    onQuantityChange,
    toggleQrScanner,
    onQrScan,
    onQrScanError,
    startVoiceListening,
    stopVoiceListening,
    fillFromVoiceTranscript,
    fillFromPhoto,
    applyAiPhotoSuggestion,
    cancelAiPhotoSuggestion,
    submit,
    clearForm,
    openCreateBoxWithReturn,
    goBack,
  },
}) => {
  return (
    <Layout title={title}>
      <div className={styles.root}>
        <div className={styles.photoHero}>
          <ProductPhotoSlider
            photo={photo}
            photos={photos}
            alt={name || "preview"}
            className={styles.imageSlider}
            trackClassName={styles.imageSliderTrack}
            imageClassName={styles.image}
            emptyClassName={styles.image}
            eagerFirstImage
            onActiveIndexChange={onPhotoSliderActiveIndexChange}
          />
          <div className={styles.photoControls}>
            <label
              className={`${styles.photoAction} ${!canAddMorePhotos ? styles.photoActionDisabled : ""}`}
              title={
                canAddMorePhotos
                  ? "Добавить фото"
                  : "Достигнут лимит фото для товара"
              }
            >
              <input
                type="file"
                accept="image/*"
                onChange={(event) => void onPhotoFileChange(event)}
                className={styles.photoUploadInput}
                disabled={!canAddMorePhotos}
              />
              <Icon name="Plus" size={IconSize.medium} />
            </label>
            <button
              type="button"
              className={styles.photoAction}
              onClick={removeActivePhoto}
              disabled={!canRemoveActivePhoto}
              title="Удалить текущее фото"
            >
              <Icon name="Minus" size={IconSize.medium} />
            </button>
          </div>
        </div>
        <div className={styles.photoMeta}>
          <span className={styles.photoCounter}>{photoCounterLabel}</span>
          {isPhotoLoading && (
            <span className={styles.hint}>загружаем изображение...</span>
          )}
        </div>

        <FieldSet title={"наименование"}>
          <Input
            value={name}
            onChange={onNameChange}
            placeholder="Например: Молоко"
          />
        </FieldSet>

        <FieldSet title={"описание"}>
          <Input
            value={description}
            onChange={onDescriptionChange}
            placeholder="Короткое описание"
          />
        </FieldSet>

        <FieldSet title={"голосовой ввод (AI-черновик)"}>
          <div className={styles.voiceHeader}>
            <button
              type="button"
              className={`${styles.voiceCircleButton} ${isVoiceListening ? styles.voiceCircleButtonActive : ""}`}
              onClick={() => onVoicePrimaryButtonClick()}
              disabled={isVoiceApplying || isSubmitting}
              aria-pressed={isVoiceListening}
              aria-label={
                isVoiceListening
                  ? "Остановить голосовой ввод"
                  : "Начать голосовой ввод"
              }
            >
              <span className={styles.voiceCircleCore} />
            </button>
            <div className={styles.voiceHeaderText}>
              <p className={styles.voiceTitle}>
                {isVoiceListening
                  ? "Слушаем речь"
                  : "Нажмите круглую кнопку и диктуйте"}
              </p>
              <p className={styles.voiceSubtitle}>
                {isVoiceListening
                  ? "Текст будет добавляться автоматически"
                  : "Можно также вставить или ввести текст вручную"}
              </p>
            </div>
            {isVoicePanelOpen && (
              <Button design={ButtonDesign.ghost} onClick={closeVoicePanel}>
                скрыть
              </Button>
            )}
          </div>

          {isVoicePanelOpen && (
            <>
              <div className={styles.voiceStatus}>
                <span
                  className={`${styles.voiceStatusDot} ${isVoiceListening ? styles.voiceStatusDotActive : ""}`}
                />
                {isVoiceListening ? "микрофон активен" : "микрофон выключен"}
              </div>
              <textarea
                className={styles.voiceTextarea}
                value={voiceTranscript}
                onChange={onVoiceTranscriptChange}
                placeholder={
                  isVoiceSupported
                    ? "Продиктуйте текст или введите его вручную"
                    : "Браузер не поддерживает голосовой ввод. Введите текст вручную."
                }
                rows={5}
              />
              <div className={styles.voiceButtons}>
                <Button
                  design={ButtonDesign.secondary}
                  onClick={() =>
                    isVoiceListening
                      ? stopVoiceListening()
                      : startVoiceListening()
                  }
                  disabled={
                    isVoiceListening
                      ? !canStopVoiceListening
                      : !canStartVoiceListening
                  }
                >
                  {isVoiceListening ? "пауза" : "продолжить диктовку"}
                </Button>
                <Button
                  design={ButtonDesign.primary}
                  onClick={() => void fillFromVoiceTranscript()}
                  disabled={!canApplyVoiceTranscript}
                  isLoading={isVoiceApplying}
                >
                  заполнить форму
                </Button>
                <Button
                  design={ButtonDesign.basicGray}
                  onClick={clearVoiceTranscript}
                  disabled={!voiceTranscript.trim()}
                >
                  очистить текст
                </Button>
              </div>
            </>
          )}
          {voiceInfo && <p className={styles.hint}>{voiceInfo}</p>}
          {voiceError && <p className={styles.error}>{voiceError}</p>}
        </FieldSet>

        <FieldSet title={"photo"}>
          <Button
            design={ButtonDesign.primary}
            onClick={() => void fillFromPhoto()}
            disabled={!canApplyPhotoAutofill}
            isLoading={isVoiceApplying}
          >
            заполнить по фото
          </Button>
          <p className={styles.hint}>
            Если есть текст диктовки, AI объединит фото и текст в один черновик.
          </p>
          {!isAiPhotoAutofillEnabled && (
            <p className={styles.hint}>
              AI-по-фото выключен в настройках приложения.
            </p>
          )}
        </FieldSet>

        <FieldSet title={"типы (#теги)"}>
          <TypeTagsInput
            tags={typeTags}
            suggestions={typeTagSuggestions}
            inputValue={typeTagInput}
            onInputValueChange={setTypeTagInput}
            onAddTags={addTypeTags}
            onRemoveTag={removeTypeTag}
            datalistId="add-product-type-tags"
            placeholder="#еда #молочка"
          />
        </FieldSet>

        <FieldSet title={"коробка (обязательно)"}>
          <BoxReferenceInput
            value={parentId}
            options={parentIdSuggestions}
            onValueChange={setParentIdInput}
            placeholder="Например: Холодильник"
            searchPlaceholder="Найти коробку"
            isLoading={isReferenceLoading}
            disabled={isSubmitting}
          />
          {parentIdSuggestions.length === 0 ? (
            <p className={styles.error}>
              Нет доступных коробок. Сначала создайте коробку.
            </p>
          ) : (
            <p className={styles.hint}>
              Товар всегда создается внутри выбранной коробки.
            </p>
          )}
          <Button
            design={ButtonDesign.ghost}
            onClick={openCreateBoxWithReturn}
            disabled={isSubmitting}
          >
            создать коробку и вернуться к товару
          </Button>
        </FieldSet>

        <div className={styles.dates}>
          <FieldSet title={"дата производства"}>
            <PickADayInput
              value={manufacturingDate}
              onChangeValue={setManufacturingDateValue}
              locale="ru"
              disabled={isSubmitting}
            />
          </FieldSet>
          <FieldSet title={"срок годности"}>
            <PickADayInput
              value={expiryDate}
              onChangeValue={setExpiryDateValue}
              locale="ru"
              disabled={isSubmitting}
            />
          </FieldSet>
        </div>

        <FieldSet title={"единица измерения"}>
          <div className={styles.measureButtons}>
            <Button
              design={
                measure === Measures.quantity
                  ? ButtonDesign.primary
                  : ButtonDesign.basic
              }
              onClick={() => setMeasure(Measures.quantity)}
            >
              quantity
            </Button>
            <Button
              design={
                measure === Measures.weight
                  ? ButtonDesign.primary
                  : ButtonDesign.basic
              }
              onClick={() => setMeasure(Measures.weight)}
            >
              weight
            </Button>
            <Button
              design={
                measure === Measures.liters
                  ? ButtonDesign.primary
                  : ButtonDesign.basic
              }
              onClick={() => setMeasure(Measures.liters)}
            >
              liters
            </Button>
          </div>
        </FieldSet>

        <FieldSet title={"количество"}>
          <Input
            type="number"
            value={quantityInput}
            onChange={onQuantityChange}
            placeholder="1"
          />
        </FieldSet>

        <FieldSet title={"QR-код (опционально)"}>
          <div className={styles.qrControls}>
            <Button
              design={
                isQrScannerOpen ? ButtonDesign.secondary : ButtonDesign.basic
              }
              onClick={toggleQrScanner}
              disabled={isSubmitting || isQrScanResolving}
            >
              <Icon name="QR" size={IconSize.small} />
              {isQrScannerOpen ? "Остановить сканирование" : "Сканировать QR"}
            </Button>
          </div>

          <p className={styles.hint}>
            {qrPayloadInput
              ? `Выбран QR: ${qrPayloadInput}`
              : "Отсканируйте QR из новой партии, чтобы привязать его к товару после создания."}
          </p>
          {isQrScanResolving && <p className={styles.hint}>проверяем QR...</p>}
          {qrBindingError && <p className={styles.error}>{qrBindingError}</p>}
        </FieldSet>
        <QrScannerOverlay
          isOpen={isQrScannerOpen}
          title="Сканирование QR товара"
          description="Наведите камеру на QR из новой партии. После проверки он будет выбран для привязки к товару."
          delay={qrScannerDelay}
          selectedPayload={qrPayloadInput}
          error={qrBindingError}
          isResolving={isQrScanResolving}
          idleText="Наведите камеру на QR-код из партии печати."
          onClose={toggleQrScanner}
          onError={onQrScanError}
          onScan={onQrScan}
        />

        {isReferenceLoading && (
          <p className={styles.hint}>загружаем коробки и теги...</p>
        )}
        {referenceError && <p className={styles.error}>{referenceError}</p>}
        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.buttons}>
          <Button
            design={ButtonDesign.primary}
            onClick={() => void submit()}
            disabled={!canSubmit}
            isLoading={isSubmitting}
          >
            создать
          </Button>
          <Button design={ButtonDesign.basic} onClick={clearForm}>
            очистить
          </Button>
        </div>

        <Button design={ButtonDesign.ghost} onClick={goBack}>
          назад к главной
        </Button>
      </div>
      <ConfirmDialog
        isOpen={isAiPhotoSuggestionOpen}
        title="Применить AI-правки?"
        description={aiPhotoSuggestionDescription}
        confirmLabel="применить"
        cancelLabel="оставить как есть"
        tone="neutral"
        onConfirm={applyAiPhotoSuggestion}
        onCancel={cancelAiPhotoSuggestion}
      />
    </Layout>
  );
});
